/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChecklistItem } from '../../components/formComponent/button/form';

export interface ButtonInterface {
  type: string;
  title: string;
  name: string;
  link: string;
  isChecked: boolean;
  errors: Object;
  color: string;
  btnType: string;
  btnSize: string;
  checklistItems: ChecklistItem[];
  fontFamily: string;
  fontStyle: string;
  fontSize: string;
  fontColor: string;
  inactivefontColor: string;
  alignment: string;
  customPaddingtop: string;
  customPaddingleft: string;
  customPaddingbottom: string;
  customPaddingright: string;
  component: string;
  sectionId: string;
  ChecklistBgColor: string;
  borderColor: string;
  borderLeft: string;
  borderTop: string;
  borderRight: string;
  borderBottom: string;
  borderStyle: string;
  borderRadius: string;
  fontWeight: string;
  fontWeightForHeader: string;
  headerFontColor: string;
  checklistBodyFontStyle: string;
  checklistBodyFontSize: string;
  checklistBodyFontWeight: string;
  checkBoxColor: string;
  ChecklistBodyFontColor: string;
  checklistBodyAlignment: string;
  strike: boolean;
  bodyFontFamily: string;
}

const initialState: ButtonInterface = {
  type: '',
  title: '',
  name: '',
  link: '',
  isChecked: true,
  errors: {},
  color: '#112299',
  btnType: 'btn btn-square btn-dark',
  btnSize: '',
  checklistItems: [],
  fontFamily: 'Helvetica',
  fontStyle: '',
  fontSize: '16',
  fontColor: '#ffffff',
  inactivefontColor: '',
  alignment: 'left',
  customPaddingtop: '10',
  customPaddingleft: '10',
  customPaddingbottom: '10',
  customPaddingright: '10',
  component: '',
  sectionId: '',
  ChecklistBgColor: '#ffffff',
  borderColor: '#000000',
  borderLeft: '0',
  borderTop: '0',
  borderRight: '0',
  borderBottom: '0',
  borderStyle: 'solid',
  borderRadius: '0',
  fontWeightForHeader: 'Normal',
  fontWeight: 'Normal',
  headerFontColor: '#000000',
  checklistBodyFontStyle: '',
  checklistBodyFontSize: '16',
  checklistBodyFontWeight: 'Normal',
  checkBoxColor: '#112299',
  ChecklistBodyFontColor: '#000000',
  checklistBodyAlignment: 'left',
  strike: true,
  bodyFontFamily: 'Helvetica',
};

const buttonSlice = createSlice({
  name: 'button',
  initialState,
  reducers: {
    setTitle: (state, action: PayloadAction<{ title: string; errors: any }>) => {
      state.title = action.payload.title;
      state.errors = action.payload.errors;
    },
    setName: (state, action: PayloadAction<{ name: string; errors: any }>) => {
      state.name = action.payload.name;
      state.errors = action.payload.errors;
    },
    setLink: (state, action: PayloadAction<{ link: string; errors: any }>) => {
      state.link = action.payload.link;
      state.errors = action.payload.errors;
    },
    setCheck: (state, action: PayloadAction<{ isChecked: boolean }>) => {
      state.isChecked = action.payload.isChecked;
    },
    buttonFormError: (state, action: PayloadAction<{ errors: any }>) => {
      state.errors = action.payload.errors;
    },
    makeEdit: (state, action: PayloadAction<Partial<ButtonInterface>>) => {
      return { ...state, ...action.payload };
    },
    setBtnColor: (state, action: PayloadAction<{ color: string }>) => {
      state.color = action.payload.color;
    },
    setBtnType: (state, action: PayloadAction<{ btnType: string }>) => {
      state.btnType = action.payload.btnType;
    },
    setBtnSize: (state, action: PayloadAction<{ btnSize: string }>) => {
      state.btnSize = action.payload.btnSize;
    },
    fontType: (state, action: PayloadAction<{ fontFamily: string }>) => {
      state.fontFamily = action.payload.fontFamily;
    },
    fontStyle: (state, action: PayloadAction<{ fontStyle: string }>) => {
      state.fontStyle = action.payload.fontStyle;
    },
    fontSize: (state, action: PayloadAction<{ fontSize: string }>) => {
      state.fontSize = action.payload.fontSize;
    },
    fontColor: (state, action: PayloadAction<{ fontColor: string }>) => {
      state.fontColor = action.payload.fontColor;
    },
    inactivefontColor: (state, action: PayloadAction<{ inactivefontColor: string }>) => {
      state.inactivefontColor = action.payload.inactivefontColor;
    },
    setAlignment: (state, action: PayloadAction<{ alignment: string }>) => {
      state.alignment = action.payload.alignment;
    },
    customPaddingtop: (state, action: PayloadAction<{ customPaddingtop: string }>) => {
      state.customPaddingtop = action.payload.customPaddingtop;
    },
    customPaddingleft: (state, action: PayloadAction<{ customPaddingleft: string }>) => {
      state.customPaddingleft = action.payload.customPaddingleft;
    },
    customPaddingbottom: (state, action: PayloadAction<{ customPaddingbottom: string }>) => {
      state.customPaddingbottom = action.payload.customPaddingbottom;
    },
    customPaddingright: (state, action: PayloadAction<{ customPaddingright: string }>) => {
      state.customPaddingright = action.payload.customPaddingright;
    },
    setType: (state, action: PayloadAction<{ type: string }>) => {
      state.type = action.payload.type;
    },
    setSectionId: (state, action: PayloadAction<{ sectionId: string }>) => {
      state.sectionId = action.payload.sectionId;
    },
    addChecklistItem: (state, action: PayloadAction<{ label: string }>) => {
      state.checklistItems.push({
        id: crypto.randomUUID(),
        label: action.payload.label,
        checked: false,
      });
    },
    removeChecklistItem: (state, action) => {
      state.checklistItems = state.checklistItems.filter(
        (item) => item.id && item.id !== action.payload,
      );
    },
    updateChecklistItem: (state, action: PayloadAction<{ id: string; value: string }>) => {
      state.checklistItems = state.checklistItems.map((item) =>
        item.id === action.payload.id ? { ...item, label: action.payload.value } : item,
      );
    },
    toggleChecklistItem: (state, action: PayloadAction<{ id: string }>) => {
      state.checklistItems = state.checklistItems.map((item) =>
        item.id === action.payload.id ? { ...item, checked: !item.checked } : item,
      );
    },
    moveChecklistItemUp: (state, action: PayloadAction<number>) => {
      const index = action.payload;

      if (index > 0) {
        const items = [...state.checklistItems];
        [items[index - 1], items[index]] = [items[index], items[index - 1]];
        state.checklistItems = items;
      }
    },

    moveChecklistItemDown: (state, action: PayloadAction<number>) => {
      const index = action.payload;

      if (index < state.checklistItems.length - 1) {
        const items = [...state.checklistItems];
        [items[index], items[index + 1]] = [items[index + 1], items[index]];
        state.checklistItems = items;
      }
    },

    setDefault: () => initialState,
    SetChecklistBgColor: (state, action: PayloadAction<{ ChecklistBgColor: string }>) => {
      state.ChecklistBgColor = action.payload.ChecklistBgColor;
    },
    setBorderTop: (state, action: PayloadAction<{ borderTop: string }>) => {
      state.borderTop = action.payload.borderTop;
    },

    setBorderRight: (state, action: PayloadAction<{ borderRight: string }>) => {
      state.borderRight = action.payload.borderRight;
    },

    setBorderLeft: (state, action: PayloadAction<{ borderLeft: string }>) => {
      state.borderLeft = action.payload.borderLeft;
    },

    setBorderBottom: (state, action: PayloadAction<{ borderBottom: string }>) => {
      state.borderBottom = action.payload.borderBottom;
    },

    setBorderRadius: (state, action: PayloadAction<{ borderRadius: string }>) => {
      state.borderRadius = action.payload.borderRadius;
    },

    setBorderColor: (state, action: PayloadAction<{ borderColor: string }>) => {
      state.borderColor = action.payload.borderColor;
    },
    setBorderStyle: (state, action: PayloadAction<{ borderStyle: string }>) => {
      state.borderStyle = action.payload.borderStyle;
    },
    setFontWeight: (state, action: PayloadAction<{ fontWeight: string }>) => {
      state.fontWeightForHeader = action.payload.fontWeight;
    },
    SetHeaderFontColor: (state, action: PayloadAction<{ headerFontColor: string }>) => {
      state.headerFontColor = action.payload.headerFontColor;
    },
    ChecklistBodyFontStyle: (state, action: PayloadAction<{ fontStyle: string }>) => {
      state.checklistBodyFontStyle = action.payload.fontStyle;
    },
    ChecklistBodyFontSize: (state, action: PayloadAction<{ fontSize: string }>) => {
      state.checklistBodyFontSize = action.payload.fontSize;
    },
    ChecklistBodyFontWeight: (state, action: PayloadAction<{ fontWeight: string }>) => {
      state.checklistBodyFontWeight = action.payload.fontWeight;
    },
    CheckBoxColor: (state, action: PayloadAction<{ checkBoxColor: string }>) => {
      state.checkBoxColor = action.payload.checkBoxColor;
    },
    ChecklistBodyFontColor: (state, action: PayloadAction<{ ChecklistBodyFontColor: string }>) => {
      state.ChecklistBodyFontColor = action.payload.ChecklistBodyFontColor;
    },
    SetChecklistBodyAlignment: (state, action: PayloadAction<{ alignment: string }>) => {
      state.checklistBodyAlignment = action.payload.alignment;
    },
    setHandleStrike: (state, action: PayloadAction<{ strike: boolean }>) => {
      state.strike = action.payload.strike;
    },
    SetBodyFontFamily: (state, action: PayloadAction<{ fontFamily: string }>) => {
      state.bodyFontFamily = action.payload.fontFamily;
    },
  },
});

export const {
  setTitle,
  setName,
  setLink,
  setCheck,
  buttonFormError,
  makeEdit,
  setBtnColor,
  setBtnType,
  setBtnSize,
  fontType,
  fontStyle,
  fontSize,
  fontColor,
  inactivefontColor,
  setAlignment,
  customPaddingtop,
  customPaddingleft,
  customPaddingbottom,
  customPaddingright,
  setType,
  setSectionId,
  addChecklistItem,
  removeChecklistItem,
  updateChecklistItem,
  toggleChecklistItem,
  moveChecklistItemDown,
  moveChecklistItemUp,
  setDefault,
  SetChecklistBgColor,
  setBorderBottom,
  setBorderColor,
  setBorderLeft,
  setBorderRadius,
  setBorderRight,
  setBorderStyle,
  setBorderTop,
  setFontWeight,
  SetHeaderFontColor,
  ChecklistBodyFontStyle,
  ChecklistBodyFontSize,
  ChecklistBodyFontWeight,
  CheckBoxColor,
  ChecklistBodyFontColor,
  SetChecklistBodyAlignment,
  setHandleStrike,
  SetBodyFontFamily,
} = buttonSlice.actions;

export default buttonSlice.reducer;
